/* eslint-disable react/jsx-wrap-multilines */
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Form, Input, Button, Row, Col, Tooltip, InputNumber, Select, Avatar, Tag } from 'antd';
import { FormInstance } from 'antd/es/form';
import { InfoCircleOutlined, PlusOutlined, CloseCircleOutlined } from '@ant-design/icons';
import TransportType from '@packages/routing/enums/transportTypes';
import { AutoStacking, AutoStackingTimeSlot, ClientFeatureConfig } from '../../types/Feature';
import PackageSize from '../../types/Package';
import AutoStackingTimeSlots from './AutoStackingTimeSlots';
import FeatureStatusInput from '../Shared/Inputs/FeatureStatusInput';

interface AutoStackingFeatureFormInterfaceProps {
    formInstance: FormInstance;
    autostacking?: ClientFeatureConfig<AutoStacking>;
    isDetailMode?: boolean;
    resetFormFlag?: boolean;
    enableControls?: boolean;
}

const cardHeaderTitleStyle = { fontSize: '18px' };

const AutoStackingFeatureFormSection: React.FC<AutoStackingFeatureFormInterfaceProps> = (
    props: AutoStackingFeatureFormInterfaceProps,
): React.ReactElement => {
    const { formInstance, autostacking, isDetailMode, resetFormFlag, enableControls } = props;

    const [isFeatureDisabled, setIsFeatureDisabled] = useState<boolean>(false);

    const isNotDetailMode = !isDetailMode;

    const packageSizeOptions = Object.keys(PackageSize).map((packageSize) => (
        <Select.Option key={packageSize} value={packageSize}>
            {packageSize}
        </Select.Option>
    ));

    const transportTypeOptions = Object.values(TransportType)
        .sort()
        .map((transportType) => (
            <Select.Option key={transportType} value={transportType}>
                {transportType}
            </Select.Option>
        ));

    const renderSelectedOptions = (optionType: string): JSX.Element[] | string => {
        const data = (autostacking && autostacking?.config[optionType]) || [];
        const options = data.map((item: string, index: number) => {
            switch (optionType) {
                case 'pickupAddressFilter':
                    return (
                        <div id="pickupAddressFilter" key={item} className="my-2">
                            <Avatar className={`${isFeatureDisabled ? '' : 'bg-primary'} mr-2`} size="small">
                                {index + 1}
                            </Avatar>
                            <span className="text-secondary">{item}</span>
                        </div>
                    );
                case 'packageTypeFilter':
                    return <Tag key={item}>{item}</Tag>;
                default:
                    return [];
            }
        });
        return options.length > 0 ? options : '-';
    };

    useEffect(() => {
        setIsFeatureDisabled(true);
    }, [resetFormFlag]);

    useEffect(() => {
        if (autostacking) {
            formInstance.setFields([
                { name: 'enabled', value: autostacking.config.enabled },
                { name: 'minTimeUntilPickup', value: autostacking.config.minTimeUntilPickup },
                { name: 'maxDrivenDistance', value: autostacking.config.maxDrivenDistance },
                { name: 'packageTypeFilter', value: autostacking.config.packageTypeFilter },
                { name: 'pickupAddressFilter', value: autostacking.config.pickupAddressFilter },
                { name: 'maxStackSize', value: autostacking.config.maxStackSize },
                { name: 'transportType', value: autostacking.config.transportType || TransportType.MOTORBIKE },
                { name: 'maxTotalTime', value: autostacking.config.maxTotalTime },
                {
                    name: 'timeSlots',
                    value:
                        autostacking.config.timeSlots &&
                        autostacking.config.timeSlots.map((timeSlot: AutoStackingTimeSlot) => {
                            return {
                                ...timeSlot,
                                startTime: moment(`${moment().format('YYYY-MM-DD')}T${timeSlot.startTime}`),
                                endTime: moment(`${moment().format('YYYY-MM-DD')}T${timeSlot.endTime}`),
                            };
                        }),
                },
                { name: 'notificationSlackChannel', value: autostacking.config.notificationSlackChannel },
                { name: 'notificationSlackMessage', value: autostacking.config.notificationSlackMessage },
            ]);
            setIsFeatureDisabled(!autostacking.config.enabled);
        }
    }, [autostacking]);

    return (
        <div className="card border-0" id="autostacking-section">
            <div className="card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                    Auto stacking
                </p>
                <div className="d-inline-flex align-items-center">
                    <span className="mr-2 d-none d-sm-block">Feature is:</span>
                    <FeatureStatusInput
                        name="enabled"
                        id="webhook_enabled"
                        isDetailMode={isDetailMode}
                        enabled={autostacking?.config.enabled}
                        isFeatureDisabled={isFeatureDisabled}
                        onChange={(value): void => setIsFeatureDisabled(!value)}
                        enableControls={enableControls}
                    />
                </div>
            </div>
            <div className="card-body">
                <Row gutter={24}>
                    <Col>
                        <Form.Item
                            name="maxStackSize"
                            /* eslint-disable prettier/prettier */
                            label={
                                <>
                                    <span className="mr-1">Stack size</span>
                                    <Tooltip title="Maximum stack size">
                                        <InfoCircleOutlined className="text-muted" />
                                    </Tooltip>
                                </>
                            }
                            rules={
                                /* eslint-disable indent */
                                isNotDetailMode
                                    ? [
                                          {
                                              required: !isFeatureDisabled,
                                              message: 'Field Maximum stack size is required',
                                          },
                                      ]
                                    : []
                                /* eslint-enable indent */
                            }
                            /* eslint-enable prettier/prettier */
                        >
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    {autostacking?.config.maxStackSize || '-'}
                                </span>
                            ) : (
                                <InputNumber
                                    name="maxStackSize"
                                    title="Maximum stack size"
                                    min={0}
                                    step={1}
                                    autoComplete="off"
                                    disabled={isFeatureDisabled || !enableControls}
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col>
                        <Form.Item
                            name="minTimeUntilPickup"
                            label={
                                <>
                                    <span className="mr-2">Minimum time until pick-up (sec.)</span>
                                    <Tooltip title="Units in seconds">
                                        <InfoCircleOutlined className="text-muted" />
                                    </Tooltip>
                                </>
                            }
                            rules={
                                /* eslint-disable indent */
                                isNotDetailMode
                                    ? [
                                          {
                                              required: !isFeatureDisabled,
                                              message: 'Field Minimum time until pick-up (sec.) is required',
                                          },
                                      ]
                                    : []
                                /* eslint-enable indent */
                            }
                        >
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    {autostacking?.config.minTimeUntilPickup || '-'}
                                </span>
                            ) : (
                                <InputNumber
                                    name="minTimeUntilPickup"
                                    // id="ft-config__min-time-until-pickup"
                                    title="Minimum time until pick-up (sec.)"
                                    disabled={isFeatureDisabled || !enableControls}
                                    min={0}
                                    step={1}
                                    autoComplete="off"
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col>
                        <Form.Item
                            name="transportType"
                            label={
                                <>
                                    <span className="mr-2">Transport Type</span>
                                    <Tooltip title="Used for route resolution">
                                        <InfoCircleOutlined className="text-muted" />
                                    </Tooltip>
                                </>
                            }
                            rules={
                                /* eslint-disable indent */
                                isNotDetailMode
                                    ? [
                                          {
                                              required: !isFeatureDisabled,
                                              message: 'Field Transport Type is required',
                                          },
                                      ]
                                    : []
                                /* eslint-enable indent */
                            }
                        >
                            {isDetailMode ? (
                                <span className="ant-form-text text-secondary">
                                    {autostacking?.config.transportType || 'motorbike'}
                                </span>
                            ) : (
                                <Select
                                    className="w-100"
                                    placeholder="Select one transport type"
                                    disabled={isFeatureDisabled || !enableControls}
                                    showArrow
                                >
                                    {transportTypeOptions}
                                </Select>
                            )}
                        </Form.Item>
                    </Col>
                </Row>
            </div>
            <Row className="h6 text-secondary mb-3">Optional configuration:</Row>
            <Row gutter={24}>
                <Col span={5} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5, offset: 1 }}>
                    <Form.Item
                        name="maxDrivenDistance"
                        /* eslint-disable prettier/prettier */
                        label={
                            <>
                                <span className="mr-1">Maximum driven distance</span>
                                <Tooltip title="Units in kilometers">
                                    <InfoCircleOutlined className="text-muted" />
                                </Tooltip>
                            </>
                        }
                    >
                        {isDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                {autostacking?.config.maxDrivenDistance || '-'}
                            </span>
                        ) : (
                            <InputNumber
                                name="maxDrivenDistance"
                                title="Maximum driven distance (km.)"
                                min={0}
                                step={1}
                                autoComplete="off"
                                disabled={isFeatureDisabled || !enableControls}
                            />
                        )}
                    </Form.Item>
                </Col>
                <Col span={5} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5, offset: 1 }}>
                    <Form.Item
                        name="maxTotalTime"
                        /* eslint-disable prettier/prettier */
                        label={
                            <>
                                <span className="mr-1">Maximum total time</span>
                                <Tooltip title="Units in minutes">
                                    <InfoCircleOutlined className="text-muted" />
                                </Tooltip>
                            </>
                        }
                    >
                        {isDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                {autostacking?.config.maxTotalTime || '-'}
                            </span>
                        ) : (
                            <InputNumber
                                name="maxTotalTime"
                                title="Maximum total time (min.)"
                                min={0}
                                step={1}
                                autoComplete="off"
                                disabled={isFeatureDisabled || !enableControls}
                            />
                        )}
                    </Form.Item>
                </Col>
                <Col span={10} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 10 }} title="Package type filter">
                    <Form.Item
                        name="packageTypeFilter"
                        label={
                            <>
                                <span className="mr-1">Package type filter</span>
                                <Tooltip title="Only packages with these types will be considered">
                                    <InfoCircleOutlined className="text-muted" />
                                </Tooltip>
                            </>
                        }
                    >
                        {isDetailMode ? (
                            <span>{renderSelectedOptions('packageTypeFilter')}</span>
                        ) : (
                            <Select
                                mode="multiple"
                                className="w-100"
                                placeholder="Select one or more package types"
                                disabled={isFeatureDisabled || !enableControls}
                                allowClear
                                showArrow
                            >
                                {packageSizeOptions}
                            </Select>
                        )}
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={24}>
                <Col span={5} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5, offset: 1 }}>
                    <Form.Item
                        name="notificationSlackChannel"
                        /* eslint-disable prettier/prettier */
                        label={
                            <>
                                <span className="mr-1">Slack Channel</span>
                                <Tooltip title="The slack channel id or name to send a message when we stack packages">
                                    <InfoCircleOutlined className="text-muted" />
                                </Tooltip>
                            </>
                        }
                    >
                        {isDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                {autostacking?.config.notificationSlackChannel || '-'}
                            </span>
                        ) : (
                            <Input
                                name="notificationSlackChannel"
                                title="Slack channel"
                                disabled={isFeatureDisabled || !enableControls}
                            />
                        )}
                    </Form.Item>
                </Col>
                <Col span={16} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 16 }}>
                    <Form.Item
                        name="notificationSlackMessage"
                        /* eslint-disable prettier/prettier */
                        label={
                            <>
                                <span className="mr-1">Slack Message</span>
                                <Tooltip title="The slack message content in markdown. Use $clientReference, $deliveryRequestId, $packageIds, $firstPackageId or $trackingLink to include these informations in the message.">
                                    <InfoCircleOutlined className="text-muted" />
                                </Tooltip>
                            </>
                        }
                    >
                        {isDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                {autostacking?.config.notificationSlackMessage || '-'}
                            </span>
                        ) : (
                            <Input
                                name="notificationSlackMessage"
                                title="Slack message"
                                disabled={isFeatureDisabled || !enableControls}
                            />
                        )}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col span={21} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 21, offset: 1 }}>
                    <p className="mb-3">Pick-up address filters:</p>
                    <p className="text-secondary">Only packages with these pick-up addresses will be considered.</p>
                    {isDetailMode ? (
                        <span>{renderSelectedOptions('pickupAddressFilter')}</span>
                    ) : (
                        <Form.List name="pickupAddressFilter">
                            {(fields, { add, remove }): JSX.Element => (
                                <>
                                    {fields.map((field, index) => (
                                        <Row gutter={24} key={field.key} className="mb-3">
                                            <Col span={20} xs={{ span: 19 }} sm={{ span: 19 }} md={{ span: 20 }}>
                                                <Form.Item
                                                    name={[field.name, 'pickupAddress']}
                                                    /* eslint-disable prettier/prettier */
                                                    initialValue={
                                                        autostacking?.config.pickupAddressFilter
                                                            ? autostacking.config.pickupAddressFilter[field.key]
                                                            : undefined
                                                    }
                                                    label={
                                                        <>
                                                            <Avatar
                                                                className={`${
                                                                    isFeatureDisabled || !enableControls
                                                                        ? ''
                                                                        : 'bg-primary'
                                                                } mr-2`}
                                                                size="small"
                                                            >
                                                                {index + 1}
                                                            </Avatar>
                                                            Pick-up address
                                                        </>
                                                    }
                                                    /* eslint-enable prettier/prettier */
                                                >
                                                    <Input
                                                        name="pickupAddressFilter"
                                                        id={`pickup-address-filter_${field.key}`}
                                                        title="Pick-up address"
                                                        placeholder="Enter pick-up address"
                                                        disabled={isFeatureDisabled || !enableControls}
                                                        autoComplete="off"
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col
                                                span={4}
                                                xs={{ span: 1 }}
                                                sm={{ span: 1 }}
                                                md={{ span: 4 }}
                                                className="d-flex align-items-center justify-content-start"
                                            >
                                                <Form.Item name={[field.name, 'remove-button']} className="m-0">
                                                    <Button
                                                        danger
                                                        type="link"
                                                        title="Remove"
                                                        className="align-items-center d-flex"
                                                        name={`btn-remove-pickup-address__${index}`}
                                                        id={`btn-remove-pickup-address__${index}`}
                                                        tabIndex={-1}
                                                        icon={<CloseCircleOutlined />}
                                                        disabled={isFeatureDisabled || !enableControls}
                                                        onClick={(): void => remove(field.name)}
                                                    >
                                                        <span className="d-none d-sm-block">Remove</span>
                                                    </Button>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    ))}
                                    <Button
                                        type="dashed"
                                        title="Add pick-up address"
                                        name="btn-add-pickup-address"
                                        id="btn-add-pickup-address"
                                        className={`${
                                            isFeatureDisabled || !enableControls ? '' : 'text-primary'
                                        } d-flex align-items-center justify-content-center w-100 mt-4`}
                                        disabled={isFeatureDisabled || !enableControls}
                                        icon={<PlusOutlined />}
                                        onClick={(): void => {
                                            add();
                                        }}
                                    >
                                        Add pick-up address
                                    </Button>
                                </>
                            )}
                        </Form.List>
                    )}
                </Col>
            </Row>
            <div className="card-body">
                <Row className="h6 text-secondary mb-3">Time Slots Configuration:</Row>

                <Form.List name="timeSlots">
                    {(fields, { add, remove }): JSX.Element => (
                        <>
                            {fields.map(
                                (field): JSX.Element => (
                                    <AutoStackingTimeSlots
                                        key={field.key}
                                        field={field}
                                        timeSlots={autostacking?.config.timeSlots}
                                        removeHandler={remove}
                                        isFeatureDisabled={isFeatureDisabled}
                                        isNotDetailMode={isNotDetailMode}
                                        enableControls={enableControls}
                                    />
                                ),
                            )}
                            {!isDetailMode && (
                                <Row>
                                    <Col md={{ span: 4, offset: 1 }}>
                                        <Button
                                            title="Add Time Slot"
                                            className={`${
                                                isFeatureDisabled || !enableControls ? '' : 'text-primary'
                                            } d-flex align-items-center justify-content-center mt-4`}
                                            disabled={isFeatureDisabled || !enableControls}
                                            icon={<PlusOutlined />}
                                            onClick={(): void => {
                                                add();
                                            }}
                                        >
                                            Add Time Slot
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </>
                    )}
                </Form.List>
            </div>
        </div>
    );
};

export default AutoStackingFeatureFormSection;
